import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload, FileUploadSelectEvent } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle } from "../../../constants/constants";
import {
  appDialogContentStyle,
  appDialogHeaderStyle,
  appInnerCardStyle,
  appMutedTextStyle,
  appWideDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

const DAILY_LOG_IMPORT_ACCEPT = ".pdf,.doc,.docx,.txt,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const DAILY_LOG_IMPORT_ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "txt"];
const DAILY_LOG_IMPORT_MAX_FILE_SIZE_IN_BYTES = 10 * 1024 * 1024;
const INCIDENT_TYPE_OPTIONS = [
  { label: "Fall", value: "fall" },
  { label: "Injury", value: "injury" },
  { label: "Behavior Event", value: "behavior_event" },
  { label: "Medication Issue", value: "medication_issue" },
  { label: "Elopement Risk", value: "elopement_risk" },
  { label: "Emergency", value: "emergency" },
  { label: "Other", value: "other" },
];

type BehaviorOption = {
  behaviorKey: string;
  behaviorTitle: string;
  sectionCategory?: string;
};

type ResidentDailyLogImportDialogProps = {
  visible: boolean;
  residentId: string;
  behaviorOptions?: BehaviorOption[];
  onHide: () => void;
  onImportCompleted?: () => Promise<void> | void;
};

const readFileAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const result = String(fileReader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    fileReader.onerror = () => reject(new Error("Unable to read the selected file."));
    fileReader.readAsDataURL(file);
  });

const formatDateTimeLocalInput = (value?: string) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 16);
};

const toIsoString = (value?: string) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const addMinutesToLocalInput = (value: string, minutes: number) => {
  const baseDate = value ? new Date(value) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    return "";
  }
  const nextDate = new Date(baseDate.getTime() + minutes * 60000);
  const localDate = new Date(nextDate.getTime() - (nextDate.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 16);
};

const formatDateTimeSummary = (value?: string) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatElapsedTimeLabel = (elapsedSeconds: number) => {
  const safeSeconds = Math.max(0, Number(elapsedSeconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  if (minutes <= 0) {
    return `${seconds}s elapsed`;
  }
  return `${minutes}m ${String(seconds).padStart(2, "0")}s elapsed`;
};

const formatDateTimeForDisplay = (value?: string) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const QUICK_END_TIME_OPTIONS = [
  { label: "1 hr later", minutes: 60 },
  { label: "2 hr later", minutes: 120 },
  { label: "5 hr later", minutes: 300 },
  { label: "10 hr later", minutes: 600 },
  { label: "15 hr later", minutes: 900 },
  { label: "20 hr later", minutes: 1200 },
];

const buildMissingFieldMessage = (
  items: any[],
  getLabel: (item: any, index: number) => string,
  getMissingFields: (item: any) => string[],
) => {
  const invalidItems = items
    .map((item, index) => ({
      label: getLabel(item, index),
      missingFields: getMissingFields(item),
    }))
    .filter((item) => item.missingFields.length > 0);

  if (!invalidItems.length) {
    return "";
  }

  return invalidItems
    .slice(0, 6)
    .map((item) => `${item.label}: missing ${item.missingFields.join(", ")}`)
    .join(" | ");
};

const getProposalTagSeverity = (needsReview: boolean, proposalType: string) => {
  if (needsReview) {
    return "warning";
  }
  if (proposalType === "incident") {
    return "danger";
  }
  if (proposalType === "daily_note") {
    return "info";
  }
  return "success";
};

const getFriendlyImportJobStatus = (status: string) => {
  const normalizedStatus = String(status || "").trim().toUpperCase();
  if (!normalizedStatus) {
    return "";
  }
  if (normalizedStatus === "SUBMITTED") {
    return "Queued";
  }
  if (normalizedStatus === "IN_PROGRESS") {
    return "In progress";
  }
  if (normalizedStatus === "COMPLETED") {
    return "Ready";
  }
  if (normalizedStatus === "FAILED") {
    return "Needs attention";
  }
  return status;
};

const getBehaviorMissingFields = (item: any) => {
  const missingFields: string[] = [];
  if (!String(item?.matchedBehaviorKey || "").trim()) {
    missingFields.push("behavior");
  }
  if (!toIsoString(item?.startedAtLocal)) {
    missingFields.push("start time");
  }
  if (!item?.isOngoing && !toIsoString(item?.endedAtLocal)) {
    missingFields.push("end time");
  }
  return missingFields;
};

const getIncidentMissingFields = (item: any) => {
  const missingFields: string[] = [];
  if (!String(item?.incidentType || "").trim()) {
    missingFields.push("incident type");
  }
  if (!toIsoString(item?.occurredAtLocal)) {
    missingFields.push("occurred at");
  }
  if (!String(item?.details || "").trim()) {
    missingFields.push("what happened");
  }
  return missingFields;
};

const getDailyNoteMissingFields = (item: any) => {
  const missingFields: string[] = [];
  if (!String(item?.details || "").trim()) {
    missingFields.push("daily note");
  }
  return missingFields;
};

const isBehaviorItemReady = (item: any) => getBehaviorMissingFields(item).length === 0;
const isIncidentItemReady = (item: any) => getIncidentMissingFields(item).length === 0;
const isDailyNoteItemReady = (item: any) => getDailyNoteMissingFields(item).length === 0;
const shouldAutoSelectReadyItem = (sectionKey: "behaviorLogs" | "incidentReports" | "dailyNotes", item: any) => {
  if (sectionKey === "behaviorLogs") {
    return isBehaviorItemReady(item);
  }
  if (sectionKey === "incidentReports") {
    return isIncidentItemReady(item);
  }
  return isDailyNoteItemReady(item);
};

const ResidentDailyLogImportDialog = ({
  visible,
  residentId,
  behaviorOptions,
  onHide,
  onImportCompleted,
}: ResidentDailyLogImportDialogProps) => {
  const { backendAPIStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [fileInputKey, setFileInputKey] = useState<number>(0);
  const [previewData, setPreviewData] = useState<any>(null);
  const [importJobStatus, setImportJobStatus] = useState<string>("");
  const [importJobSubStatus, setImportJobSubStatus] = useState<string>("");
  const [resolvedBehaviorOptions, setResolvedBehaviorOptions] = useState<BehaviorOption[]>(Array.isArray(behaviorOptions) ? behaviorOptions : []);
  const [activePreviewTab, setActivePreviewTab] = useState<number>(0);
  const [previewStartedAtMs, setPreviewStartedAtMs] = useState<number>(0);
  const [elapsedPreviewSeconds, setElapsedPreviewSeconds] = useState<number>(0);
  const [lastImportSummary, setLastImportSummary] = useState<any>(null);
  const [showUploadChooser, setShowUploadChooser] = useState<boolean>(false);

  useEffect(() => {
    if (!visible) {
      resetSelectedFileUpload();
      setPreviewData(null);
      setPreviewing(false);
      setSaving(false);
      setImportJobStatus("");
      setImportJobSubStatus("");
      setActivePreviewTab(0);
      setPreviewStartedAtMs(0);
      setElapsedPreviewSeconds(0);
      setLastImportSummary(null);
      setShowUploadChooser(false);
      setResolvedBehaviorOptions(Array.isArray(behaviorOptions) ? behaviorOptions : []);
    }
  }, [visible, behaviorOptions]);

  useEffect(() => {
    if (!previewing || !previewStartedAtMs) {
      return;
    }

    setElapsedPreviewSeconds(Math.max(0, Math.floor((Date.now() - previewStartedAtMs) / 1000)));
    const intervalId = window.setInterval(() => {
      setElapsedPreviewSeconds(Math.max(0, Math.floor((Date.now() - previewStartedAtMs) / 1000)));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [previewStartedAtMs, previewing]);

  useEffect(() => {
    const loadLastImportSummary = async () => {
      if (!visible || !residentId) {
        return;
      }
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getResidentDailyLogImportLastSummary(residentId);
        setLastImportSummary(response?.summary || null);
      } catch (error) {
        console.error("Unable to load last daily log import summary", error);
        setLastImportSummary(null);
      }
    };

    void loadLastImportSummary();
  }, [backendAPIStore, residentId, visible]);

  useEffect(() => {
    const loadBehaviorOptions = async () => {
      const providedOptions = Array.isArray(behaviorOptions) ? behaviorOptions.filter((option) => option?.behaviorKey) : [];
      if (providedOptions.length) {
        setResolvedBehaviorOptions(providedOptions);
        return;
      }
      if (!visible || !residentId) {
        return;
      }

      try {
        await backendAPIStore.init();
        const snapshot = await backendAPIStore.getResidentBehaviorSnapshot(residentId);
        const nextOptions = (Array.isArray(snapshot?.behaviors) ? snapshot.behaviors : [])
          .map((row: any) => ({
            behaviorKey: String(row?.behaviorKey || "").trim(),
            behaviorTitle: String(row?.behaviorTitle || "").trim() || String(row?.behaviorKey || "").trim() || "Behavior",
            sectionCategory: String(row?.sectionCategory || "").trim() || "Psych/Social & Cognitive",
          }))
          .filter((option: BehaviorOption) => option.behaviorKey);
        setResolvedBehaviorOptions(nextOptions);
      } catch (error) {
        console.error("Unable to load behavior options for daily log import", error);
        setResolvedBehaviorOptions([]);
      }
    };

    void loadBehaviorOptions();
  }, [backendAPIStore, behaviorOptions, residentId, visible]);

  const behaviorDropdownOptions = useMemo(
    () => resolvedBehaviorOptions.map((option) => ({
      label: option.sectionCategory
        ? `${option.behaviorTitle} (${option.sectionCategory})`
        : option.behaviorTitle,
      value: option.behaviorKey,
    })),
    [resolvedBehaviorOptions],
  );
  const chooseFileOptions = useMemo(() => ({
    icon: "pi pi-fw pi-upload",
    label: selectedFile ? "Change File" : "Choose File",
    className: "daily-log-import-choose-btn p-button-rounded",
    style: {
      background: "#1d4ed8",
      borderColor: "#1e40af",
      color: "#ffffff",
      fontWeight: 700,
      boxShadow: "0 12px 28px rgba(30, 64, 175, 0.22)",
    },
  }), [selectedFile]);
  const resetSelectedFileUpload = () => {
    setSelectedFile(null);
    setFileInputKey((previousState) => previousState + 1);
    fileUploadRef.current?.clear();
  };

  const startNewFileSelection = () => {
    setPreviewData(null);
    setPreviewing(false);
    setSaving(false);
    setImportJobStatus("");
    setImportJobSubStatus("");
    setActivePreviewTab(0);
    setPreviewStartedAtMs(0);
    setElapsedPreviewSeconds(0);
    setShowUploadChooser(true);
    resetSelectedFileUpload();
  };

  const loadPendingItemsFromLastImportSummary = async (summary: any) => {
    const jobId = String(summary?.jobId || "").trim();
    if (!jobId) {
      return;
    }

    const pendingBehaviorPreviewIds = new Set((Array.isArray(summary?.pendingBehaviorPreviewIds) ? summary.pendingBehaviorPreviewIds : []).map((value: any) => String(value || "").trim()).filter(Boolean));
    const pendingIncidentPreviewIds = new Set((Array.isArray(summary?.pendingIncidentPreviewIds) ? summary.pendingIncidentPreviewIds : []).map((value: any) => String(value || "").trim()).filter(Boolean));
    const pendingDailyNotePreviewIds = new Set((Array.isArray(summary?.pendingDailyNotePreviewIds) ? summary.pendingDailyNotePreviewIds : []).map((value: any) => String(value || "").trim()).filter(Boolean));

    setPreviewData(null);
    setPreviewing(true);
    setImportJobStatus("IN_PROGRESS");
    setImportJobSubStatus("Loading the last import review");
    setPreviewStartedAtMs(Date.now());
    setElapsedPreviewSeconds(0);

    try {
      await backendAPIStore.init();
      const jobResponse = await backendAPIStore.getResidentDailyLogImportPreviewJob(residentId, jobId);
      if (String(jobResponse?.status || "").trim().toLowerCase() !== "completed" || !jobResponse?.preview) {
        throw new Error("The last import review is not ready to reopen yet.");
      }

      const hydratedPreview = hydratePreviewData({
        ...jobResponse.preview,
        jobId,
      });

      const nextPreviewData = {
        ...hydratedPreview,
        behaviorLogs: (hydratedPreview?.behaviorLogs || []).filter((item: any) => pendingBehaviorPreviewIds.has(String(item?.previewId || "").trim())),
        incidentReports: (hydratedPreview?.incidentReports || []).filter((item: any) => pendingIncidentPreviewIds.has(String(item?.previewId || "").trim())),
        dailyNotes: (hydratedPreview?.dailyNotes || []).filter((item: any) => pendingDailyNotePreviewIds.has(String(item?.previewId || "").trim())),
      };
      nextPreviewData.summary = {
        ...(hydratedPreview?.summary || {}),
        behaviorLogCount: nextPreviewData.behaviorLogs.length,
        incidentReportCount: nextPreviewData.incidentReports.length,
        dailyNoteCount: nextPreviewData.dailyNotes.length,
        reviewRequiredCount: [
          ...nextPreviewData.behaviorLogs,
          ...nextPreviewData.incidentReports,
          ...nextPreviewData.dailyNotes,
        ].filter((item: any) => item?.needsReview).length,
      };
      setPreviewData(nextPreviewData);
      setImportJobStatus("COMPLETED");
      setImportJobSubStatus("Last review reopened");
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to reopen the last import review.",
        life: 5000,
      });
    } finally {
      setPreviewing(false);
    }
  };

  const hydratePreviewData = (response: any) => ({
    ...response,
    importJobId: String(response?.jobId || "").trim(),
    importSourceFileName: String(response?.fileName || "").trim(),
    importOriginalSummary: {
      totalParsedEntries: Number(response?.summary?.totalParsedEntries || 0),
      behaviorLogCount: Number(response?.summary?.behaviorLogCount || 0),
      incidentReportCount: Number(response?.summary?.incidentReportCount || 0),
      dailyNoteCount: Number(response?.summary?.dailyNoteCount || 0),
    },
    behaviorLogs: (Array.isArray(response?.behaviorLogs) ? response.behaviorLogs : []).map((item: any) => ({
      ...item,
      selected: isBehaviorItemReady({
        ...item,
        startedAtLocal: formatDateTimeLocalInput(item?.startedAtLocal),
        endedAtLocal: formatDateTimeLocalInput(item?.endedAtLocal),
      }),
      startedAtLocal: formatDateTimeLocalInput(item?.startedAtLocal),
      endedAtLocal: formatDateTimeLocalInput(item?.endedAtLocal),
      showExactEndTime: false,
      showEndTimeActions: !formatDateTimeLocalInput(item?.endedAtLocal),
    })),
    incidentReports: (Array.isArray(response?.incidentReports) ? response.incidentReports : []).map((item: any) => ({
      ...item,
      selected: isIncidentItemReady({
        ...item,
        occurredAtLocal: formatDateTimeLocalInput(item?.occurredAtLocal),
      }),
      occurredAtLocal: formatDateTimeLocalInput(item?.occurredAtLocal),
    })),
    dailyNotes: (Array.isArray(response?.dailyNotes) ? response.dailyNotes : []).map((item: any) => ({
      ...item,
      selected: isDailyNoteItemReady(item),
    })),
  });

  const updatePreviewSectionItem = (sectionKey: "behaviorLogs" | "incidentReports" | "dailyNotes", previewId: string, updater: (item: any) => any) => {
    setPreviewData((previousState: any) => {
      if (!previousState) {
        return previousState;
      }
      return {
        ...previousState,
        [sectionKey]: (previousState?.[sectionKey] || []).map((item: any) => {
          if (item?.previewId !== previewId) {
            return item;
          }
          const nextItem = updater(item);
          if (Object.prototype.hasOwnProperty.call(nextItem || {}, "selected")) {
            return nextItem;
          }
          return {
            ...nextItem,
            selected: shouldAutoSelectReadyItem(sectionKey, nextItem),
          };
        }),
      };
    });
  };

  const updateAllSectionSelections = (sectionKey: "behaviorLogs" | "incidentReports" | "dailyNotes", selected: boolean) => {
    setPreviewData((previousState: any) => {
      if (!previousState) {
        return previousState;
      }
      const getMissingFieldsForSection = (item: any) => {
        if (sectionKey === "behaviorLogs") {
          return getBehaviorMissingFields(item);
        }
        if (sectionKey === "incidentReports") {
          return getIncidentMissingFields(item);
        }
        return getDailyNoteMissingFields(item);
      };
      return {
        ...previousState,
        [sectionKey]: (previousState?.[sectionKey] || []).map((item: any) => ({
          ...item,
          selected: selected ? getMissingFieldsForSection(item).length === 0 : false,
        })),
      };
    });
  };

  const areAllSectionItemsSelected = (sectionKey: "behaviorLogs" | "incidentReports" | "dailyNotes") => {
    const items = Array.isArray(previewData?.[sectionKey]) ? previewData[sectionKey] : [];
    const readyItems = items.filter((item: any) => {
      if (sectionKey === "behaviorLogs") {
        return getBehaviorMissingFields(item).length === 0;
      }
      if (sectionKey === "incidentReports") {
        return getIncidentMissingFields(item).length === 0;
      }
      return getDailyNoteMissingFields(item).length === 0;
    });
    return readyItems.length > 0 && readyItems.every((item: any) => Boolean(item?.selected));
  };

  const selectAllItems = () => {
    setPreviewData((previousState: any) => {
      if (!previousState) {
        return previousState;
      }
      return {
        ...previousState,
        behaviorLogs: (previousState?.behaviorLogs || []).map((item: any) => ({ ...item, selected: getBehaviorMissingFields(item).length === 0 })),
        incidentReports: (previousState?.incidentReports || []).map((item: any) => ({ ...item, selected: getIncidentMissingFields(item).length === 0 })),
        dailyNotes: (previousState?.dailyNotes || []).map((item: any) => ({ ...item, selected: getDailyNoteMissingFields(item).length === 0 })),
      };
    });
  };

  const onFileSelected = async (file: File | null) => {
    setSelectedFile(file);
    setShowUploadChooser(false);
    setPreviewData(null);
    setImportJobStatus("");
    setImportJobSubStatus("");
    setActivePreviewTab(0);
    setPreviewStartedAtMs(0);
    setElapsedPreviewSeconds(0);
    if (!file) {
      return;
    }

    const extension = file.name.includes(".")
      ? file.name.split(".").pop()?.toLowerCase() || ""
      : "";
    if (!DAILY_LOG_IMPORT_ALLOWED_EXTENSIONS.includes(extension)) {
      toast.current?.show({
        severity: "warn",
        summary: "Unsupported file type",
        detail: "Choose a PDF, DOCX, DOC, or TXT progress notes file.",
        life: 4000,
      });
      resetSelectedFileUpload();
      return;
    }
    if (file.size > DAILY_LOG_IMPORT_MAX_FILE_SIZE_IN_BYTES) {
      toast.current?.show({
        severity: "warn",
        summary: "File too large",
        detail: "The uploaded file must be 10 MB or smaller.",
        life: 4000,
      });
      resetSelectedFileUpload();
      return;
    }

    setPreviewing(true);
    setPreviewStartedAtMs(Date.now());
    setElapsedPreviewSeconds(0);
    try {
      await backendAPIStore.init();
      const fileContentBase64 = await readFileAsBase64(file);
      const response = await backendAPIStore.previewResidentDailyLogImport(residentId, {
        fileName: file.name,
        mimeType: file.type || "",
        fileContentBase64,
      });
      const nextJobId = String(response?.jobId || "").trim();
      if (!nextJobId) {
        throw new Error("We could not start the review job for this file.");
      }
      setImportJobStatus(String(response?.status || "submitted").trim());
      setImportJobSubStatus(String(response?.message || "We started reviewing the notes. PDF OCR may take a bit longer.").trim());

      const maxAttempts = 60;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const jobResponse = await backendAPIStore.getResidentDailyLogImportPreviewJob(residentId, nextJobId);
        const responseStatus = String(jobResponse?.status || "").trim().toLowerCase();
        setImportJobStatus(String(jobResponse?.jobStatus || response?.status || "").trim());
        setImportJobSubStatus(String(jobResponse?.jobSubStatus || "").trim());

        if (responseStatus === "failed") {
          throw new Error(String(jobResponse?.errorMessage || "Unable to review the uploaded progress notes."));
        }

        if (responseStatus === "completed" && jobResponse?.preview) {
          setPreviewData(hydratePreviewData({
            ...jobResponse.preview,
            jobId: nextJobId,
          }));
          setImportJobSubStatus("Review ready");
          return;
        }
      }

      throw new Error("The review is taking longer than expected. Please try again in a moment.");
    } catch (error: any) {
      resetSelectedFileUpload();
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to preview the uploaded progress notes.",
        life: 5000,
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleFileUploadSelect = (event: FileUploadSelectEvent) => {
    const file = event.files?.[0] || null;
    void onFileSelected(file);
  };

  const importSelectedItems = async () => {
    if (!previewData) {
      return;
    }

    const selectedBehaviorLogs = (previewData?.behaviorLogs || []).filter((item: any) => item?.selected);
    const selectedIncidentReports = (previewData?.incidentReports || []).filter((item: any) => item?.selected);
    const selectedDailyNotes = (previewData?.dailyNotes || []).filter((item: any) => item?.selected);

    const invalidBehaviorLogs = selectedBehaviorLogs.filter((item: any) => getBehaviorMissingFields(item).length > 0);
    const invalidIncidentReports = selectedIncidentReports.filter((item: any) => getIncidentMissingFields(item).length > 0);
    const invalidDailyNotes = selectedDailyNotes.filter((item: any) => getDailyNoteMissingFields(item).length > 0);

    if (
      invalidBehaviorLogs.length
      || invalidIncidentReports.length
      || invalidDailyNotes.length
    ) {
      const detailParts = [
        buildMissingFieldMessage(
          invalidBehaviorLogs,
          (item) => `${item?.dateKey || "Behavior log"} ${item?.startTimeLabel || ""}`.trim(),
          getBehaviorMissingFields,
        ),
        buildMissingFieldMessage(
          invalidIncidentReports,
          (item) => `${item?.title || "Incident report"}${item?.dateKey ? ` (${item.dateKey})` : ""}`,
          getIncidentMissingFields,
        ),
        buildMissingFieldMessage(
          invalidDailyNotes,
          (item) => item?.title || `Daily note ${item?.dateKey || ""}`.trim(),
          getDailyNoteMissingFields,
        ),
      ].filter(Boolean);

      toast.current?.show({
        severity: "warn",
        summary: "Review required",
        detail: detailParts.join(" | ") || "Some selected items are missing required details. Please review them before importing.",
        life: 8000,
      });
      return;
    }

    const totalSelected = selectedBehaviorLogs.length + selectedIncidentReports.length + selectedDailyNotes.length;
    if (!totalSelected) {
      toast.current?.show({
        severity: "warn",
        summary: "Nothing selected",
        detail: "Select at least one suggested item to import.",
        life: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      const selectedBehaviorPreviewIds = new Set(selectedBehaviorLogs.map((item: any) => String(item?.previewId || "").trim()).filter(Boolean));
      const selectedIncidentPreviewIds = new Set(selectedIncidentReports.map((item: any) => String(item?.previewId || "").trim()).filter(Boolean));
      const selectedDailyNotePreviewIds = new Set(selectedDailyNotes.map((item: any) => String(item?.previewId || "").trim()).filter(Boolean));

      await backendAPIStore.init();
      await Promise.all([
        ...selectedBehaviorLogs.map((item: any) =>
          backendAPIStore.createResidentBehaviorLogEntry(residentId, {
            behaviorKey: item.matchedBehaviorKey,
            behaviorTitle: item.matchedBehaviorTitle,
            behaviorCategory: item.matchedBehaviorCategory || "",
            sourceStaffName: item.sourceStaffName || "",
            sourceStaffInitials: item.sourceStaffInitials || "",
            residentPresentBehavior: item.residentPresentBehavior || "",
            instructionsGiven: item.instructionsGiven || "",
            residentResponse: item.residentResponse || "",
            notes: item.notes || "",
            startedAt: toIsoString(item.startedAtLocal),
            endedAt: item.isOngoing ? "" : toIsoString(item.endedAtLocal),
            isOngoing: item.isOngoing === true,
            status: item.isOngoing ? "open" : "resolved",
          }),
        ),
        ...selectedIncidentReports.map((item: any) =>
          backendAPIStore.createResidentIncidentReport(residentId, {
            title: item.title || "Incident Report",
            occurredAt: toIsoString(item.occurredAtLocal),
            incidentType: item.incidentType || "other",
            sourceStaffName: item.sourceStaffName || "",
            sourceStaffInitials: item.sourceStaffInitials || "",
            details: item.details || "",
            actionTaken: item.actionTaken || "",
            notificationsMade: item.notificationsMade || "",
            notes: item.notes || "",
            status: "submitted",
          }),
        ),
        ...selectedDailyNotes.map((item: any) =>
          backendAPIStore.createResidentDailyNote(residentId, {
            title: item.title || "Progress Note",
            occurredAt: toIsoString(`${item.dateKey}T12:00`),
            sourceStaffName: item.sourceStaffName || "",
            sourceStaffInitials: item.sourceStaffInitials || "",
            details: item.details || "",
            notes: item.notes || "",
            status: "final",
          }),
        ),
      ]);

      const originalSummary = previewData?.importOriginalSummary || {};
      const nextLastImportSummary = {
        jobId: String(previewData?.importJobId || "").trim(),
        fileName: String(previewData?.fileName || previewData?.importSourceFileName || "").trim(),
        importedAt: new Date().toISOString(),
        totalDetectedCount: Number(originalSummary?.totalParsedEntries || 0),
        totalBehaviorLogCount: Number(originalSummary?.behaviorLogCount || 0),
        totalIncidentReportCount: Number(originalSummary?.incidentReportCount || 0),
        totalDailyNoteCount: Number(originalSummary?.dailyNoteCount || 0),
        importedBehaviorLogCount: Number(originalSummary?.behaviorLogCount || 0) - Math.max((previewData?.behaviorLogs || []).length - selectedBehaviorLogs.length, 0),
        importedIncidentReportCount: Number(originalSummary?.incidentReportCount || 0) - Math.max((previewData?.incidentReports || []).length - selectedIncidentReports.length, 0),
        importedDailyNoteCount: Number(originalSummary?.dailyNoteCount || 0) - Math.max((previewData?.dailyNotes || []).length - selectedDailyNotes.length, 0),
        pendingBehaviorLogCount: Math.max((previewData?.behaviorLogs || []).length - selectedBehaviorLogs.length, 0),
        pendingIncidentReportCount: Math.max((previewData?.incidentReports || []).length - selectedIncidentReports.length, 0),
        pendingDailyNoteCount: Math.max((previewData?.dailyNotes || []).length - selectedDailyNotes.length, 0),
        pendingBehaviorPreviewIds: (previewData?.behaviorLogs || [])
          .filter((item: any) => !selectedBehaviorPreviewIds.has(String(item?.previewId || "").trim()))
          .map((item: any) => String(item?.previewId || "").trim())
          .filter(Boolean),
        pendingIncidentPreviewIds: (previewData?.incidentReports || [])
          .filter((item: any) => !selectedIncidentPreviewIds.has(String(item?.previewId || "").trim()))
          .map((item: any) => String(item?.previewId || "").trim())
          .filter(Boolean),
        pendingDailyNotePreviewIds: (previewData?.dailyNotes || [])
          .filter((item: any) => !selectedDailyNotePreviewIds.has(String(item?.previewId || "").trim()))
          .map((item: any) => String(item?.previewId || "").trim())
          .filter(Boolean),
        totalImportedCount:
          (Number(originalSummary?.behaviorLogCount || 0) - Math.max((previewData?.behaviorLogs || []).length - selectedBehaviorLogs.length, 0))
          + (Number(originalSummary?.incidentReportCount || 0) - Math.max((previewData?.incidentReports || []).length - selectedIncidentReports.length, 0))
          + (Number(originalSummary?.dailyNoteCount || 0) - Math.max((previewData?.dailyNotes || []).length - selectedDailyNotes.length, 0)),
        totalPendingCount:
          Math.max((previewData?.behaviorLogs || []).length - selectedBehaviorLogs.length, 0)
          + Math.max((previewData?.incidentReports || []).length - selectedIncidentReports.length, 0)
          + Math.max((previewData?.dailyNotes || []).length - selectedDailyNotes.length, 0),
      };
      await backendAPIStore.updateResidentDailyLogImportLastSummary(residentId, nextLastImportSummary);
      setLastImportSummary(nextLastImportSummary);

      toast.current?.show({
        severity: "success",
        summary: "Daily log imported",
        detail: `${totalSelected} item${totalSelected === 1 ? "" : "s"} imported successfully.`,
        life: 3000,
      });
      let remainingItemCount = 0;
      setPreviewData((previousState: any) => {
        if (!previousState) {
          return previousState;
        }

        const remainingBehaviorLogs = (previousState?.behaviorLogs || []).filter((item: any) => !selectedBehaviorPreviewIds.has(String(item?.previewId || "").trim()));
        const remainingIncidentReports = (previousState?.incidentReports || []).filter((item: any) => !selectedIncidentPreviewIds.has(String(item?.previewId || "").trim()));
        const remainingDailyNotes = (previousState?.dailyNotes || []).filter((item: any) => !selectedDailyNotePreviewIds.has(String(item?.previewId || "").trim()));
        remainingItemCount = remainingBehaviorLogs.length + remainingIncidentReports.length + remainingDailyNotes.length;

        return {
          ...previousState,
          behaviorLogs: remainingBehaviorLogs,
          incidentReports: remainingIncidentReports,
          dailyNotes: remainingDailyNotes,
          summary: {
            ...(previousState?.summary || {}),
            behaviorLogCount: remainingBehaviorLogs.length,
            incidentReportCount: remainingIncidentReports.length,
            dailyNoteCount: remainingDailyNotes.length,
            reviewRequiredCount: [
              ...remainingBehaviorLogs,
              ...remainingIncidentReports,
              ...remainingDailyNotes,
            ].filter((item: any) => item?.needsReview).length,
          },
        };
      });
      await onImportCompleted?.();
      if (!remainingItemCount) {
        onHide();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to import the selected items.",
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = useMemo(() => {
    if (!previewData) {
      return 0;
    }
    return [
      ...(previewData?.behaviorLogs || []),
      ...(previewData?.incidentReports || []),
      ...(previewData?.dailyNotes || []),
    ].filter((item: any) => item?.selected).length;
  }, [previewData]);
  const behaviorLogCount = Array.isArray(previewData?.behaviorLogs) ? previewData.behaviorLogs.length : 0;
  const incidentReportCount = Array.isArray(previewData?.incidentReports) ? previewData.incidentReports.length : 0;
  const dailyNoteCount = Array.isArray(previewData?.dailyNotes) ? previewData.dailyNotes.length : 0;

  return (
    <Dialog
      header={renderSurfaceHeader(
        "Add Previous Daily Logs",
        "Bring in last month's daily notes from a file and review suggested behavior logs, incident reports, and daily notes before saving them in AFH Hub.",
        "pi pi-upload",
      )}
      visible={visible}
      style={appWideDialogStyle}
      headerStyle={appDialogHeaderStyle}
      contentStyle={appDialogContentStyle}
      modal
      onHide={onHide}
      footer={(
        <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic", flex: "1 1 320px" }}>
            {AI_MESSAGE_FOR_COMPLIANCE}
          </p>
          <Button
            type="button"
            label={saving ? "Importing..." : `Import Selected${selectedCount ? ` (${selectedCount})` : ""}`}
            icon="pi pi-check"
            severity="warning"
            rounded
            loading={saving}
            disabled={saving || !previewData || !selectedCount}
            onClick={() => { void importSelectedItems(); }}
          />
        </div>
      )}
    >
      <Toast ref={toast} />
      <style>
        {`
          .daily-log-import-choose-btn,
          .daily-log-import-choose-btn .p-button-label,
          .daily-log-import-choose-btn .p-button-icon {
            color: #ffffff !important;
          }
        `}
      </style>
      <div className="flex flex-column gap-3">
        {!previewData && !selectedFile && (showUploadChooser || !lastImportSummary) ? (
          <div style={appInnerCardStyle}>
            <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
              <div>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Upload daily notes file</div>
                <div className="mt-1" style={appMutedTextStyle}>
                  Use a daily log file from the previous month, up to 10 MB. We will help turn the file into behavior logs, incident reports, and daily notes for AFH Hub, and you can review everything before saving.
                </div>
              </div>
            </div>
            <div className="mt-3">
              <FileUpload
                key={fileInputKey}
                ref={fileUploadRef}
                mode="basic"
                name="dailyLogImportFile"
                accept={DAILY_LOG_IMPORT_ACCEPT}
                maxFileSize={DAILY_LOG_IMPORT_MAX_FILE_SIZE_IN_BYTES}
                customUpload
                auto={false}
                chooseOptions={chooseFileOptions}
                onSelect={handleFileUploadSelect}
              />
            </div>
          </div>
        ) : null}

        {previewing && !previewData ? (
          <div style={appInnerCardStyle}>
            <div style={{ display: "grid", gap: "0.35rem", justifyItems: "center", textAlign: "center" }}>
              <div className="flex flex-column align-items-center gap-2" style={appMutedTextStyle}>
                <ProgressSpinner style={{ width: "20px", height: "20px" }} strokeWidth="6" />
                <span>Reviewing progress notes</span>
              </div>
              {importJobStatus ? (
                <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                  Status: {getFriendlyImportJobStatus(importJobStatus)}
                </div>
              ) : null}
              {elapsedPreviewSeconds > 0 ? (
                <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                  {formatElapsedTimeLabel(elapsedPreviewSeconds)}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {!previewing && !previewData && lastImportSummary && !showUploadChooser ? (
          <div style={{ ...appInnerCardStyle, display: "grid", gap: "0.45rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Last import</div>
            <div style={appMutedTextStyle}>
              {lastImportSummary?.fileName || "Imported file"}
              {lastImportSummary?.importedAt ? ` • ${formatDateTimeForDisplay(lastImportSummary.importedAt)}` : ""}
            </div>
            <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
              <Tag value={`${Number(lastImportSummary?.totalImportedCount || 0)} imported`} severity="success" rounded />
              <Tag value={`${Number(lastImportSummary?.totalPendingCount || 0)} pending`} severity={Number(lastImportSummary?.totalPendingCount || 0) > 0 ? "warning" : "info"} rounded />
              <Tag value={`${Number(lastImportSummary?.totalDetectedCount || 0)} detected`} severity="info" rounded />
            </div>
            <div className="flex gap-2 flex-wrap">
              {Number(lastImportSummary?.totalPendingCount || 0) > 0 ? (
                <Button
                  type="button"
                  label="Resume review"
                  icon="pi pi-refresh"
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => { void loadPendingItemsFromLastImportSummary(lastImportSummary); }}
                />
              ) : null}
              <Button
                type="button"
                label="Upload new file"
                icon="pi pi-upload"
                severity="secondary"
                rounded
                outlined
                onClick={startNewFileSelection}
              />
            </div>
          </div>
        ) : null}

        {previewData ? (
          <>
            <div className="flex justify-content-end">
              <Button
                type="button"
                label="Upload new file"
                icon="pi pi-upload"
                severity="secondary"
                rounded
                outlined
                onClick={startNewFileSelection}
              />
            </div>
            <div style={{ ...appInnerCardStyle, display: "grid", gap: "0.5rem" }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>{previewData?.fileName || "Imported file"}</div>
              <div style={{ display: "grid", gap: "0.35rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Detected</div>
                <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
                  <Tag value={`${previewData?.summary?.totalParsedEntries || 0} time-stamped notes`} severity="info" rounded />
                  <Tag value={`${previewData?.summary?.behaviorLogCount || 0} behavior logs`} severity="success" rounded />
                  <Tag value={`${previewData?.summary?.incidentReportCount || 0} incident reports`} severity="danger" rounded />
                  <Tag value={`${previewData?.summary?.dailyNoteCount || 0} daily notes`} severity="warning" rounded />
                </div>
              </div>
            </div>

            <div style={{ ...appInnerCardStyle, paddingBottom: "0.5rem" }}>
              <TabView activeIndex={activePreviewTab} onTabChange={(event) => setActivePreviewTab(event.index)}>
                <TabPanel header={`Behavior Logs (${behaviorLogCount})`} />
                <TabPanel header={`Incident Reports (${incidentReportCount})`} />
                <TabPanel header={`Progress Notes (${dailyNoteCount})`} />
              </TabView>
            </div>

            {activePreviewTab === 0 ? (
              <div style={{ ...appInnerCardStyle, display: "grid", gap: "1rem" }}>
                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Behavior Logs</div>
                    <div className="mt-1" style={appMutedTextStyle}>
                      Confirm which notes should become behavior-log episodes and adjust the suggested behavior or times if needed.
                    </div>
                  </div>
                  {(previewData?.behaviorLogs || []).length > 0 ? (
                    <Button
                      type="button"
                      label={areAllSectionItemsSelected("behaviorLogs") ? "Deselect all behavior logs" : "Select all behavior logs"}
                      severity="secondary"
                      rounded
                      outlined
                      onClick={() => updateAllSectionSelections("behaviorLogs", !areAllSectionItemsSelected("behaviorLogs"))}
                    />
                  ) : null}
                </div>
                {(previewData?.behaviorLogs || []).length === 0 ? (
                  <div style={appMutedTextStyle}>No behavior log suggestions were found.</div>
                ) : (
                  <div style={{ display: "grid", gap: "0.9rem" }}>
                    {(previewData?.behaviorLogs || []).map((item: any) => (
                      (() => {
                        const isReady = isBehaviorItemReady(item);
                        const isSelected = Boolean(item?.selected);
                        return (
                          <div
                            key={item.previewId}
                            style={{
                              padding: "0.9rem",
                              borderRadius: "16px",
                              border: `1px solid ${isReady ? (isSelected ? "#22c55e" : "#86efac") : "#fca5a5"}`,
                              background: isReady ? (isSelected ? "#dcfce7" : "#f0fdf4") : "#fff7f7",
                            }}
                          >
                            <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                              <div>
                                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                  {item?.dateKey || "Unknown date"} • {item?.startTimeLabel || "Unknown time"}
                                </div>
                                <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                                  {[
                                    item?.matchedBehaviorTitle || "Behavior needs review",
                                    item?.sourceStaffName ? `${item.sourceStaffName}${item?.sourceStaffInitials ? ` (${item.sourceStaffInitials})` : ""}` : "",
                                    formatDateTimeSummary(item?.startedAtLocal) || "Start time not available",
                                  ].filter(Boolean).join(" • ")}
                                </div>
                              </div>
                              <Tag value={item?.needsReview ? "Needs Review" : "Suggested"} severity={getProposalTagSeverity(Boolean(item?.needsReview), "behavior")} rounded />
                            </div>
                            {isReady ? (
                              <div className="mt-2 flex align-items-center gap-2">
                                <Checkbox
                                  inputId={`${item.previewId}-selected`}
                                  checked={!!item?.selected}
                                  onChange={(event) => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                    ...currentItem,
                                    selected: !!event.checked,
                                  }))}
                                />
                                <label htmlFor={`${item.previewId}-selected`} style={{ fontWeight: 600, color: "#166534" }}>
                                  Include this behavior log
                                </label>
                              </div>
                            ) : (
                              <div className="mt-2" style={{ color: "#b91c1c", fontSize: "0.84rem", fontWeight: 600 }}>
                                Complete: {getBehaviorMissingFields(item).join(", ")}
                              </div>
                            )}
                            {item?.reviewReason ? (
                              <div className="mt-2" style={{ color: "#92400e", fontSize: "0.88rem" }}>
                                {item.reviewReason}
                              </div>
                            ) : null}
                            <div className="grid mt-3">
                              <div className="col-12 md:col-6">
                                <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Behavior</label>
                                <Dropdown
                                  value={item?.matchedBehaviorKey || ""}
                                  options={behaviorDropdownOptions}
                                  placeholder="Select a care-plan behavior"
                                  className="w-full"
                                  showClear
                                  onChange={(event) => {
                                    const selectedOption = resolvedBehaviorOptions.find((option) => option.behaviorKey === String(event.value || ""));
                                    updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                      ...currentItem,
                                      matchedBehaviorKey: String(event.value || ""),
                                      matchedBehaviorTitle: selectedOption?.behaviorTitle || "",
                                      matchedBehaviorCategory: selectedOption?.sectionCategory || "",
                                    }));
                                  }}
                                />
                              </div>
                              <div className="col-12 md:col-3">
                                <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Start time</label>
                                <div
                                  style={{
                                    minHeight: "42px",
                                    border: "1px solid #cbd5e1",
                                    borderRadius: "12px",
                                    padding: "0.75rem 0.9rem",
                                    background: "#f8fafc",
                                    color: "#0f172a",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {formatDateTimeSummary(item?.startedAtLocal) || "Start time not available"}
                                </div>
                              </div>
                              <div className="col-12">
                                <div
                                  style={{
                                    border: !item?.endedAtLocal ? "1px solid #fca5a5" : "1px solid #86efac",
                                    background: !item?.endedAtLocal ? "#fef2f2" : "#f0fdf4",
                                    borderRadius: "14px",
                                    padding: "0.85rem",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "block",
                                      fontWeight: 700,
                                      color: !item?.endedAtLocal ? "#b91c1c" : "#166534",
                                      marginBottom: "0.35rem",
                                    }}
                                  >
                                    End time
                                  </label>
                                  {!item?.endedAtLocal ? (
                                    <div style={{ color: "#b91c1c", fontSize: "0.84rem", fontWeight: 600, marginBottom: "0.55rem" }}>
                                      End time is required before this behavior log can be imported.
                                    </div>
                                  ) : null}
                                  {item?.endedAtLocal && !item?.showEndTimeActions ? (
                                    <div style={{ display: "grid", gap: "0.45rem" }}>
                                      <div
                                        style={{
                                          minHeight: "42px",
                                          border: "1px solid #86efac",
                                          borderRadius: "12px",
                                          padding: "0.75rem 0.9rem",
                                          background: "#ffffff",
                                          color: "#166534",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        {formatDateTimeSummary(item?.endedAtLocal) || "End time not available"}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                          ...currentItem,
                                          showEndTimeActions: true,
                                          showExactEndTime: false,
                                        }))}
                                        style={{
                                          border: "none",
                                          padding: 0,
                                          background: "transparent",
                                          color: "#c2410c",
                                          fontWeight: 600,
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                          justifySelf: "start",
                                        }}
                                      >
                                        Edit end time
                                      </button>
                                    </div>
                                  ) : (
                                    <div style={{ display: "grid", gap: "0.7rem", width: "100%" }}>
                                      <div className="flex gap-2 flex-wrap">
                                        {QUICK_END_TIME_OPTIONS.map((option) => (
                                          <Button
                                            key={`${item.previewId}-${option.minutes}`}
                                            type="button"
                                            label={option.label}
                                            severity="warning"
                                            rounded
                                            outlined
                                            style={{ fontSize: "0.78rem", padding: "0.35rem 0.7rem" }}
                                            onClick={() => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                              ...currentItem,
                                              endedAtLocal: addMinutesToLocalInput(currentItem?.startedAtLocal || "", option.minutes),
                                              showEndTimeActions: false,
                                              showExactEndTime: false,
                                            }))}
                                          />
                                        ))}
                                      </div>
                                      <div style={{ display: "grid", gap: "0.45rem", width: "100%" }}>
                                        {!item?.showExactEndTime ? (
                                          <button
                                            type="button"
                                            onClick={() => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                              ...currentItem,
                                              showExactEndTime: true,
                                            }))}
                                            style={{
                                              border: "none",
                                              padding: 0,
                                              background: "transparent",
                                              color: "#c2410c",
                                              fontWeight: 600,
                                              cursor: "pointer",
                                              textDecoration: "underline",
                                              justifySelf: "start",
                                            }}
                                          >
                                            Set exact time
                                          </button>
                                        ) : (
                                          <div style={{ display: "grid", gap: "0.45rem", width: "100%", marginTop: "0.15rem" }}>
                                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.1rem" }}>Exact time</label>
                                            <div style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                                              <input
                                                type="datetime-local"
                                                className="p-inputtext p-component w-full"
                                                style={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}
                                                value={item?.endedAtLocal || ""}
                                                onChange={(event) => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                                  ...currentItem,
                                                  endedAtLocal: event.target.value,
                                                  showEndTimeActions: !String(event.target.value || "").trim(),
                                                }))}
                                              />
                                            </div>
                                            <div className="flex gap-3 flex-wrap">
                                              {item?.endedAtLocal ? (
                                                <button
                                                  type="button"
                                                  onClick={() => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                                    ...currentItem,
                                                    showEndTimeActions: false,
                                                    showExactEndTime: false,
                                                  }))}
                                                  style={{
                                                    border: "none",
                                                    padding: 0,
                                                    background: "transparent",
                                                    color: "#c2410c",
                                                    fontWeight: 600,
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                  }}
                                                >
                                                  Save end time
                                                </button>
                                              ) : null}
                                              <button
                                                type="button"
                                                onClick={() => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                                  ...currentItem,
                                                  showExactEndTime: false,
                                                }))}
                                                style={{
                                                  border: "none",
                                                  padding: 0,
                                                  background: "transparent",
                                                  color: "#64748b",
                                                  fontWeight: 600,
                                                  cursor: "pointer",
                                                  textDecoration: "underline",
                                                  justifySelf: "start",
                                                }}
                                              >
                                                Hide exact time
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-12 md:col-6">
                                <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Present behavior</label>
                                <InputTextarea
                                  value={item?.residentPresentBehavior || ""}
                                  rows={2}
                                  className="w-full"
                                  onChange={(event) => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                    ...currentItem,
                                    residentPresentBehavior: event.target.value,
                                  }))}
                                />
                              </div>
                              <div className="col-12 md:col-6">
                                <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Instructions given</label>
                                <InputTextarea
                                  value={item?.instructionsGiven || ""}
                                  rows={2}
                                  className="w-full"
                                  onChange={(event) => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                    ...currentItem,
                                    instructionsGiven: event.target.value,
                                  }))}
                                />
                              </div>
                              <div className="col-12 md:col-6">
                                <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Resident response</label>
                                <InputTextarea
                                  value={item?.residentResponse || ""}
                                  rows={2}
                                  className="w-full"
                                  onChange={(event) => updatePreviewSectionItem("behaviorLogs", item.previewId, (currentItem) => ({
                                    ...currentItem,
                                    residentResponse: event.target.value,
                                  }))}
                                />
                              </div>
                            </div>
                            <div className="mt-3" style={{ display: "grid", gap: "0.4rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Note from the file</div>
                              {(item?.sourceEntries || []).map((sourceEntry: any) => (
                                <div key={`${item.previewId}-${sourceEntry.entryIndex}`} style={{ ...appInnerCardStyle, whiteSpace: "pre-wrap", padding: "0.75rem 0.85rem" }}>
                                  <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem" }}>
                                    #{sourceEntry.entryIndex} • {sourceEntry.dateKey} • {sourceEntry.timeLabel}
                                  </div>
                                  <div className="mt-1" style={appMutedTextStyle}>{sourceEntry.staffOnDuty ? `Staff on duty: ${sourceEntry.staffOnDuty}` : "Staff on duty not found"}</div>
                                  <div className="mt-2" style={{ color: "#0f172a" }}>{sourceEntry.noteText}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {activePreviewTab === 1 ? (
              <div style={{ ...appInnerCardStyle, display: "grid", gap: "1rem" }}>
                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Incident Reports</div>
                    <div className="mt-1" style={appMutedTextStyle}>
                      Review serious or unusual events separately from routine notes.
                    </div>
                  </div>
                  {(previewData?.incidentReports || []).length > 0 ? (
                    <Button
                      type="button"
                      label={areAllSectionItemsSelected("incidentReports") ? "Deselect all incident reports" : "Select all incident reports"}
                      severity="secondary"
                      rounded
                      outlined
                      onClick={() => updateAllSectionSelections("incidentReports", !areAllSectionItemsSelected("incidentReports"))}
                    />
                  ) : null}
                </div>
                {(previewData?.incidentReports || []).length === 0 ? (
                  <div style={appMutedTextStyle}>No incident-report suggestions were found.</div>
                ) : (
                  <div style={{ display: "grid", gap: "0.9rem" }}>
                    {(previewData?.incidentReports || []).map((item: any) => (
                      <div
                        key={item.previewId}
                        style={{
                          padding: "0.9rem",
                          borderRadius: "16px",
                          border: `1px solid ${item?.selected ? "#86efac" : (isIncidentItemReady(item) ? "#fecaca" : "#fca5a5")}`,
                          background: item?.selected ? "#f0fdf4" : (isIncidentItemReady(item) ? "#fff1f2" : "#fff7f7"),
                        }}
                      >
                        <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                          <div>
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>
                              {item?.title || "Incident Report"}
                            </div>
                            <div className="mt-1" style={appMutedTextStyle}>
                              {item?.dateKey || "Unknown date"} • {item?.occurredTimeLabel || "Unknown time"}
                            </div>
                            {item?.sourceStaffName ? (
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                                Staff from file: {item.sourceStaffName}{item?.sourceStaffInitials ? ` (${item.sourceStaffInitials})` : ""}
                              </div>
                            ) : null}
                          </div>
                          <Tag value={item?.needsReview ? "Needs Review" : "Suggested"} severity={getProposalTagSeverity(Boolean(item?.needsReview), "incident")} rounded />
                        </div>
                        {isIncidentItemReady(item) ? (
                          <div className="mt-2 flex align-items-center gap-2">
                            <Checkbox
                              inputId={`${item.previewId}-selected`}
                              checked={!!item?.selected}
                              onChange={(event) => updatePreviewSectionItem("incidentReports", item.previewId, (currentItem) => ({
                                ...currentItem,
                                selected: !!event.checked,
                              }))}
                            />
                            <label htmlFor={`${item.previewId}-selected`} style={{ fontWeight: 600, color: item?.selected ? "#166534" : "#0f172a" }}>
                              Include this incident report
                            </label>
                          </div>
                        ) : (
                          <div className="mt-2" style={{ color: "#b91c1c", fontSize: "0.84rem", fontWeight: 600 }}>
                            Complete: {getIncidentMissingFields(item).join(", ")}
                          </div>
                        )}
                        <div className="grid mt-3">
                          <div className="col-12 md:col-5">
                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Incident type</label>
                            <Dropdown
                              value={item?.incidentType || ""}
                              options={INCIDENT_TYPE_OPTIONS}
                              placeholder="Select incident type"
                              className="w-full"
                              onChange={(event) => updatePreviewSectionItem("incidentReports", item.previewId, (currentItem) => ({
                                ...currentItem,
                                incidentType: String(event.value || ""),
                              }))}
                            />
                          </div>
                          <div className="col-12 md:col-7">
                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Title</label>
                            <InputText
                              value={item?.title || ""}
                              className="w-full"
                              onChange={(event) => updatePreviewSectionItem("incidentReports", item.previewId, (currentItem) => ({
                                ...currentItem,
                                title: event.target.value,
                              }))}
                            />
                          </div>
                          <div className="col-12 md:col-4">
                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Occurred at</label>
                            <input
                              type="datetime-local"
                              className="p-inputtext p-component w-full"
                              value={item?.occurredAtLocal || ""}
                              onChange={(event) => updatePreviewSectionItem("incidentReports", item.previewId, (currentItem) => ({
                                ...currentItem,
                                occurredAtLocal: event.target.value,
                              }))}
                            />
                          </div>
                          <div className="col-12">
                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>What happened</label>
                            <InputTextarea
                              value={item?.details || ""}
                              rows={3}
                              className="w-full"
                              onChange={(event) => updatePreviewSectionItem("incidentReports", item.previewId, (currentItem) => ({
                                ...currentItem,
                                details: event.target.value,
                              }))}
                            />
                          </div>
                          <div className="col-12 md:col-6">
                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Action taken</label>
                            <InputTextarea
                              value={item?.actionTaken || ""}
                              rows={2}
                              className="w-full"
                              onChange={(event) => updatePreviewSectionItem("incidentReports", item.previewId, (currentItem) => ({
                                ...currentItem,
                                actionTaken: event.target.value,
                              }))}
                            />
                          </div>
                          <div className="col-12 md:col-6">
                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Notifications made</label>
                            <InputTextarea
                              value={item?.notificationsMade || ""}
                              rows={2}
                              className="w-full"
                              onChange={(event) => updatePreviewSectionItem("incidentReports", item.previewId, (currentItem) => ({
                                ...currentItem,
                                notificationsMade: event.target.value,
                              }))}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {activePreviewTab === 2 ? (
              <div style={{ ...appInnerCardStyle, display: "grid", gap: "1rem" }}>
                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Progress Notes</div>
                    <div className="mt-1" style={appMutedTextStyle}>
                      One consolidated daily note is suggested for each day represented in the imported file.
                    </div>
                  </div>
                  {(previewData?.dailyNotes || []).length > 0 ? (
                    <Button
                      type="button"
                      label={areAllSectionItemsSelected("dailyNotes") ? "Deselect all daily notes" : "Select all daily notes"}
                      severity="secondary"
                      rounded
                      outlined
                      onClick={() => updateAllSectionSelections("dailyNotes", !areAllSectionItemsSelected("dailyNotes"))}
                    />
                  ) : null}
                </div>
                {(previewData?.dailyNotes || []).length === 0 ? (
                  <div style={appMutedTextStyle}>No daily-note suggestions were found.</div>
                ) : (
                  <div style={{ display: "grid", gap: "0.9rem" }}>
                    {(previewData?.dailyNotes || []).map((item: any) => (
                      <div
                        key={item.previewId}
                        style={{
                          padding: "0.9rem",
                          borderRadius: "16px",
                          border: `1px solid ${item?.selected ? "#86efac" : (isDailyNoteItemReady(item) ? "#bfdbfe" : "#fca5a5")}`,
                          background: item?.selected ? "#f0fdf4" : (isDailyNoteItemReady(item) ? "#eff6ff" : "#fff7f7"),
                        }}
                      >
                        <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                          <div>
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>
                              {item?.title || `Progress Note - ${item?.dateKey || "Day"}`}
                            </div>
                            <div className="mt-1" style={appMutedTextStyle}>{item?.dateKey || "Unknown date"}</div>
                            {item?.sourceStaffName ? (
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                                Staff from file: {item.sourceStaffName}{item?.sourceStaffInitials ? ` (${item.sourceStaffInitials})` : ""}
                              </div>
                            ) : null}
                          </div>
                          <Tag value={item?.needsReview ? "Needs Review" : "Suggested"} severity={getProposalTagSeverity(Boolean(item?.needsReview), "daily_note")} rounded />
                        </div>
                        {isDailyNoteItemReady(item) ? (
                          <div className="mt-2 flex align-items-center gap-2">
                            <Checkbox
                              inputId={`${item.previewId}-selected`}
                              checked={!!item?.selected}
                              onChange={(event) => updatePreviewSectionItem("dailyNotes", item.previewId, (currentItem) => ({
                                ...currentItem,
                                selected: !!event.checked,
                              }))}
                            />
                            <label htmlFor={`${item.previewId}-selected`} style={{ fontWeight: 600, color: item?.selected ? "#166534" : "#0f172a" }}>
                              Include this daily note
                            </label>
                          </div>
                        ) : (
                          <div className="mt-2" style={{ color: "#b91c1c", fontSize: "0.84rem", fontWeight: 600 }}>
                            Complete: {getDailyNoteMissingFields(item).join(", ")}
                          </div>
                        )}
                        <div className="grid mt-3">
                          <div className="col-12">
                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Title</label>
                            <InputText
                              value={item?.title || ""}
                              className="w-full"
                              onChange={(event) => updatePreviewSectionItem("dailyNotes", item.previewId, (currentItem) => ({
                                ...currentItem,
                                title: event.target.value,
                              }))}
                            />
                          </div>
                          <div className="col-12">
                            <label style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Daily note</label>
                            <InputTextarea
                              value={item?.details || ""}
                              rows={5}
                              className="w-full"
                              onChange={(event) => updatePreviewSectionItem("dailyNotes", item.previewId, (currentItem) => ({
                                ...currentItem,
                                details: event.target.value,
                              }))}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
};

export default ResidentDailyLogImportDialog;
